import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, Search, Minus, Plus, Download, Edit3, Trash2, Eye, EyeOff } from 'lucide-react';
import { useProducts } from '../hooks/useProducts';
import { useOrders } from '../hooks/useOrders';
import { useProtectedAdminAction } from '../hooks/useProtectedAdminAction';
import { useAuditLog } from '../hooks/useAuditLog';
import { useOrdersEnabled } from '../hooks/useOrdersEnabled';
import { useAdmins } from '../hooks/useAdmins';
import { shipOrder } from '../lib/shipOrder';
import { canDeleteOrder } from '../lib/orderStatus';
import ShippingSettings from '../components/ShippingSettings';
import SiteContentEditor from '../components/SiteContentEditor';
import { DISPUTE_STATUS_LABELS, isDisputeOpen } from '../lib/orderLabels';
import ProductForm from '../components/ProductForm';
import ProductEditOverlay from '../components/ProductEditOverlay';
import ConfirmationModal from '../components/utils/ConfirmationModal';
import { NewProductForm, Product, ProductVariant, VariantForm } from '../types/product';
import { allVariants, activeVariants, itemName } from '../lib/variants';
import type { ProductUpdates } from '../hooks/useProducts';
import { Order, OrderStatus, PaymentStatus } from '../types/order';

type View = 'oggi' | 'ordini' | 'catalogo' | 'contenuti' | 'impostazioni';

const STATUS_META: Record<OrderStatus, { label: string; bg: string; fg: string; next: OrderStatus | null; nextLabel: string }> = {
  pending: { label: 'IN ATTESA', bg: 'hsl(var(--peach))', fg: 'hsl(var(--peach-foreground))', next: 'confirmed', nextLabel: 'Conferma' },
  confirmed: { label: 'CONFERMATO', bg: 'hsl(var(--tone-confirmed))', fg: 'hsl(var(--primary))', next: 'processing', nextLabel: 'Prepara' },
  processing: { label: 'IN PREPARAZIONE', bg: 'hsl(var(--tone-processing))', fg: 'hsl(var(--primary))', next: 'shipped', nextLabel: 'Segna spedito' },
  shipped: { label: 'SPEDITO', bg: 'hsl(var(--eco))', fg: 'hsl(var(--eco-foreground))', next: 'delivered', nextLabel: 'Consegnato' },
  delivered: { label: 'CONSEGNATO', bg: 'hsl(var(--eco))', fg: 'hsl(var(--eco-foreground))', next: 'completed', nextLabel: 'Chiudi' },
  completed: { label: 'COMPLETATO', bg: 'hsl(var(--secondary))', fg: 'hsl(var(--foreground) / 0.75)', next: null, nextLabel: '' },
  cancelled: { label: 'ANNULLATO', bg: 'hsl(var(--secondary))', fg: 'hsl(var(--danger))', next: null, nextLabel: '' },
  refunded: { label: 'RIMBORSATO', bg: 'hsl(var(--secondary))', fg: 'hsl(var(--muted-foreground))', next: null, nextLabel: '' },
};

const PAY_META: Record<PaymentStatus, { label: string; dot: string }> = {
  paid: { label: 'Incassato', dot: 'hsl(var(--eco-foreground))' },
  pending: { label: 'In attesa', dot: 'hsl(var(--action))' },
  failed: { label: 'Fallito', dot: 'hsl(var(--danger))' },
  refunded: { label: 'Rimborsato', dot: 'hsl(var(--muted-foreground))' },
  cancelled: { label: 'Annullato', dot: 'hsl(var(--muted-foreground))' },
};

const STATUS_CHIPS: { key: OrderStatus | ''; label: string }[] = [
  { key: '', label: 'TUTTI' },
  { key: 'pending', label: 'IN ATTESA' },
  { key: 'confirmed', label: 'CONFERMATI' },
  { key: 'processing', label: 'IN PREPARAZIONE' },
  { key: 'shipped', label: 'SPEDITI' },
  { key: 'completed', label: 'CHIUSI' },
];

const eur = (n: number) => '€ ' + Number(n || 0).toFixed(2).replace('.', ',');

const ago = (iso: string) => {
  const hours = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (hours < 1) return 'pochi minuti fa';
  if (hours < 24) return Math.round(hours) + ' ore fa';
  const days = Math.round(hours / 24);
  return days === 1 ? 'ieri' : days + ' giorni fa';
};

// Stesso codice di email, pagina di conferma e traccia ordine: ultimi 8
// caratteri in maiuscolo.
const shortId = (id: string) => '#' + id.slice(-8).toUpperCase();

// Una sola griglia per intestazione e righe della lista ordini, così le
// colonne restano allineate.
// Sotto lg ogni riga diventa una scheda: vedi le varianti lg:hidden.
const ORDERS_GRID = 'hidden lg:grid min-w-[900px] grid-cols-[32px_minmax(0,1.1fr)_minmax(0,1.4fr)_110px_150px_130px_220px] gap-3 px-5';
const CATALOG_GRID = 'hidden lg:grid min-w-[900px] grid-cols-[minmax(0,1.6fr)_100px_110px_150px_150px_110px_110px] gap-3 px-5';

const NavButton: React.FC<{ active: boolean; onClick: () => void; label: string; count?: number | string; compact?: boolean }> = ({
  active,
  onClick,
  label,
  count,
  compact,
}) => (
  <button
    onClick={onClick}
    aria-current={active ? 'page' : undefined}
    className={`flex items-center rounded-lg font-semibold transition-colors ${
      compact
        ? 'flex-none grow flex-col justify-center gap-1 px-1.5 py-1.5 text-center text-xs'
        : 'justify-between gap-2 px-3 py-2.5 text-left text-sm'
    } ${active ? 'bg-white/15 text-white' : 'text-honey-200/90 hover:bg-white/5'}`}
  >
    <span className={compact ? 'whitespace-nowrap' : undefined}>{label}</span>
    {count !== undefined && (
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${compact ? 'leading-3' : ''} ${
          active ? 'bg-action text-[#2F1500]' : 'opacity-75'
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const StatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <span
      className="inline-block rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide"
      style={{ background: meta.bg, color: meta.fg }}
    >
      {meta.label}
    </span>
  );
};

/**
 * Quantità modificabile: +/- salvano subito, un numero digitato si salva
 * uscendo dal campo o con Invio. Il valore mostrato torna a quello del DB
 * se il salvataggio fallisce o il valore non è valido.
 */
const QuantityCell: React.FC<{
  value: number;
  max?: number;
  danger?: boolean;
  label: string;
  onCommit: (next: number) => void;
}> = ({ value, max, danger, label, onCommit }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (raw: number) => {
    const next = Math.max(0, Math.min(max ?? Infinity, Math.floor(raw)));
    if (Number.isNaN(next) || next === value) {
      setDraft(String(value));
      return;
    }
    onCommit(next);
  };

  const btn =
    'flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-muted text-primary shadow-[inset_0_0_0_1px_hsl(var(--border))] lg:h-[30px] lg:w-[30px] disabled:opacity-40';

  return (
    <div className="flex items-center gap-1 lg:gap-1.5">
      <button onClick={() => commit(value - 1)} disabled={value <= 0} className={btn} aria-label={`${label}: togli uno`}>
        <Minus className="h-3.5 w-3.5" />
      </button>
      <input
        type="number"
        min={0}
        max={max}
        value={draft}
        aria-label={label}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(Number(draft))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setDraft(String(value));
        }}
        className="h-10 w-10 rounded-lg border lg:h-[30px] lg:w-[52px] border-border bg-card text-center text-[15px] font-bold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        style={{ color: danger ? 'hsl(var(--danger))' : 'hsl(var(--foreground))' }}
      />
      <button
        onClick={() => commit(value + 1)}
        disabled={max !== undefined && value >= max}
        className={btn}
        aria-label={`${label}: aggiungi uno`}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    products,
    loading: productsLoading,
    error: productsError,
    setError: setProductsError,
    fetchProducts,
    addProduct,
    updateProduct,
    saveVariants,
    updateVariantStock,
    deleteProduct,
  } = useProducts();

  const {
    orders,
    loading: ordersLoading,
    error: ordersError,
    setError: setOrdersError,
    fetchOrders,
    updateOrder,
    deleteOrder,
  } = useOrders();

  const { executeProtectedAction } = useProtectedAdminAction({ onError: (msg) => toast.error(msg) });
  const { logUpdateAction, logDeleteAction } = useAuditLog();
  const { ordersEnabled, loading: ordersSwitchLoading, setOrdersEnabled } = useOrdersEnabled();
  const { admins, loading: adminsLoading, fetchAdmins, addAdmin, removeAdmin } = useAdmins();

  // La vista sta nell'URL (?view=ordini) così il dettaglio ordine può
  // tornare alla lista e un link o un refresh riaprono la stessa vista.
  const [searchParams, setSearchParams] = useSearchParams();
  const VIEWS: View[] = ['oggi', 'ordini', 'catalogo', 'contenuti', 'impostazioni'];
  const viewParam = searchParams.get('view') as View | null;
  const view: View = viewParam && VIEWS.includes(viewParam) ? viewParam : 'oggi';
  const setView = (next: View) =>
    setSearchParams(next === 'oggi' ? {} : { view: next }, { replace: true });
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState('ora');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<Order | null>(null);
  const [confirmOrdersSwitch, setConfirmOrdersSwitch] = useState<boolean | null>(null);
  const [shippingOrder, setShippingOrder] = useState<Order | null>(null);
  const [shipTracking, setShipTracking] = useState('');
  const [shippingBusy, setShippingBusy] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [removingAdmin, setRemovingAdmin] = useState<{ user_id: string; email: string } | null>(null);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, [fetchProducts, fetchOrders]);

  useEffect(() => {
    const interval = setInterval(() => fetchOrders(), 60000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchProducts(), fetchOrders()]);
    setLastSync('ora');
    setRefreshing(false);
  };

  const clearMessages = () => {
    setProductsError(null);
    setOrdersError(null);
  };

  const error = productsError || ordersError;
  useEffect(() => {
    if (error) toast.error(error, { onDismiss: clearMessages, onAutoClose: clearMessages });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const pending = useMemo(() => orders.filter((o) => o.status === 'pending'), [orders]);
  const prep = useMemo(() => orders.filter((o) => o.status === 'confirmed' || o.status === 'processing'), [orders]);
  const readyToShip = useMemo(
    () => orders.filter((o) => o.status === 'processing' && o.payment_status === 'paid'),
    [orders]
  );
  const unpaid = useMemo(() => orders.filter((o) => o.payment_status === 'pending'), [orders]);
  const disputed = useMemo(() => orders.filter((o) => isDisputeOpen(o.dispute_status)), [orders]);
  // Un avviso per formato: "Millefiori 1 kg: 2 rimasti".
  const lowStock = useMemo(
    () => products.filter((p) => p.active).flatMap((p) => activeVariants(p).filter((v) => v.stock <= 5).map((v) => ({ product: p, variant: v }))),
    [products]
  );

  const revenuePaid = useMemo(
    () => orders.filter((o) => o.payment_status === 'paid').reduce((s, o) => s + o.total_price + o.shipping_price, 0),
    [orders]
  );
  const revenueDue = useMemo(
    () => unpaid.reduce((s, o) => s + o.total_price + o.shipping_price, 0),
    [unpaid]
  );

  const soldLast30 = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
    const counts: Record<string, number> = {};
    orders.forEach((o) => {
      if (o.status === 'cancelled' || o.status === 'refunded') return;
      if (new Date(o.created_at).getTime() < cutoff) return;
      o.items?.forEach((it) => {
        // Per formato; le righe senza formato (eliminato) contano sul prodotto.
        const key = it.variant_id ?? it.product_id;
        counts[key] = (counts[key] || 0) + it.quantity;
      });
    });
    return counts;
  }, [orders]);

  const goToOrdini = (status: OrderStatus | '') => {
    setStatusFilter(status);
    setSelected([]);
    setView('ordini');
  };

  const advanceOrder = async (o: Order) => {
    const meta = STATUS_META[o.status];
    if (!meta.next) return;
    const nextStatus = meta.next;
    if (nextStatus === 'shipped') {
      setShipTracking(o.tracking_number || '');
      setShippingOrder(o);
      return;
    }
    await executeProtectedAction(async () => {
      const ok = await updateOrder(o.id, { status: nextStatus });
      if (ok) {
        await logUpdateAction('orders', o.id, { status: o.status }, { status: nextStatus });
        toast.success(`Ordine ${shortId(o.id)} → ${STATUS_META[nextStatus].label.toLowerCase()}`);
      } else {
        toast.error("Errore durante l'aggiornamento dello stato");
      }
      return ok;
    });
  };

  const markPaid = async (o: Order) => {
    await executeProtectedAction(async () => {
      const ok = await updateOrder(o.id, { payment_status: 'paid' });
      if (ok) {
        await logUpdateAction('orders', o.id, { payment_status: o.payment_status }, { payment_status: 'paid' });
        toast.success('Pagamento registrato');
      } else {
        toast.error('Errore durante la registrazione del pagamento');
      }
      return ok;
    });
  };

  const handleDeleteOrder = async (o: Order) => {
    await executeProtectedAction(async () => {
      const ok = await deleteOrder(o.id);
      if (ok) {
        await logDeleteAction('orders', o.id, {
          customer_name: o.customer_name,
          total_price: o.total_price,
          status: o.status,
        });
        toast.success('Ordine eliminato');
        setDeletingOrder(null);
      } else {
        toast.error("Errore durante l'eliminazione dell'ordine");
      }
      return ok;
    });
  };

  const handleDeleteProduct = async (p: Product) => {
    await executeProtectedAction(async () => {
      const ok = await deleteProduct(p.id);
      if (ok) {
        await logDeleteAction('products', p.id, {
          name: p.name,
          variants: p.product_variants.map((v) => `${v.label} ${v.price}`).join(', '),
          category: p.category,
        });
        toast.success(`Prodotto "${p.name}" eliminato`);
        setDeletingProduct(null);
      } else {
        toast.error("Errore durante l'eliminazione del prodotto");
      }
      return ok;
    });
  };

  const handleSaveProductEdit = async (productId: string, updates: ProductUpdates, variants: VariantForm[]) => {
    const ok = (await updateProduct(productId, updates)) && (await saveVariants(productId, variants));
    if (ok) {
      toast.success('Prodotto aggiornato');
      setEditingProduct(null);
    } else {
      toast.error("Errore durante l'aggiornamento del prodotto");
    }
    return ok;
  };

  // Nascosto: resta qui in dashboard ma sparisce dal sito e non si può ordinare.
  const toggleVisibility = async (p: Product) => {
    const ok = await updateProduct(p.id, { active: !p.active });
    if (ok) toast.success(p.active ? `"${p.name}" nascosto dal sito` : `"${p.name}" di nuovo visibile sul sito`);
  };

  const variantName = (p: Product, v: ProductVariant) => `${p.name} ${v.label}`;

  // "+12" dagli avvisi di scorta: nuovi vasetti arrivati in magazzino e
  // messi subito in vendita online.
  const restock = async (p: Product, v: ProductVariant) => {
    const ok = await updateVariantStock(p.id, v.id, { inventory: v.inventory + 12, stock: v.stock + 12 });
    if (ok) toast.success('Giacenza aggiornata: ' + variantName(p, v));
  };

  // Magazzino: se scende sotto la quota online, la quota si abbassa con lui
  // (il DB rifiuta stock > inventory).
  const setInventory = async (p: Product, v: ProductVariant, inventory: number) => {
    const stock = Math.min(v.stock, inventory);
    const ok = await updateVariantStock(p.id, v.id, { inventory, stock });
    if (ok && stock < v.stock) toast.info(`${variantName(p, v)}: in vendita online ridotti a ${stock}`);
  };

  const setOnline = async (p: Product, v: ProductVariant, stock: number) => {
    await updateVariantStock(p.id, v.id, { stock: Math.min(stock, v.inventory) });
  };

  const openSettings = () => setView('impostazioni');

  useEffect(() => {
    if (view === 'impostazioni') fetchAdmins().catch((err) => toast.error(err.message));
  }, [view, fetchAdmins]);

  const handleShip = async (e: React.FormEvent) => {
    e.preventDefault();
    const o = shippingOrder;
    if (!o || !shipTracking.trim()) return;
    setShippingBusy(true);
    await executeProtectedAction(async () => {
      try {
        const result = await shipOrder({
          orderId: o.id,
          trackingNumber: shipTracking,
          previousStatus: o.status,
        });
        await logUpdateAction(
          'orders',
          o.id,
          { status: o.status, tracking_number: o.tracking_number },
          { status: 'shipped', tracking_number: shipTracking.trim() }
        );
        if (result.emailError) {
          toast.warning(`Ordine ${shortId(o.id)} spedito, ma l'email al cliente non è partita: ${result.emailError}`);
        } else {
          toast.success(`Ordine ${shortId(o.id)} spedito: email con il tracking inviata al cliente`);
        }
        setShippingOrder(null);
        await fetchOrders();
        return true;
      } catch (err: any) {
        toast.error(err.message || "Errore durante l'aggiornamento dell'ordine");
        return false;
      }
    });
    setShippingBusy(false);
  };

  const handleOrdersSwitch = async (enabled: boolean) => {
    await executeProtectedAction(async () => {
      try {
        await setOrdersEnabled(enabled);
        await logUpdateAction('shop_settings', 'orders_enabled', { orders_enabled: !enabled }, { orders_enabled: enabled });
        toast.success(enabled ? 'Ordini online aperti' : 'Ordini online chiusi');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setConfirmOrdersSwitch(null);
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newAdminEmail.trim();
    if (!email) return;
    setAddingAdmin(true);
    await executeProtectedAction(async () => {
      try {
        await addAdmin(email);
        toast.success(`${email} ora è amministratore`);
        setNewAdminEmail('');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setAddingAdmin(false);
  };

  const handleRemoveAdmin = async (userId: string, email: string) => {
    await executeProtectedAction(async () => {
      try {
        await removeAdmin(userId);
        toast.success(`${email} non è più amministratore`);
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setRemovingAdmin(null);
  };

  const handleAddProduct = async (data: NewProductForm) => {
    const ok = await addProduct(data);
    if (ok) {
      toast.success('Prodotto aggiunto con successo!');
      setShowAddProduct(false);
    }
    return ok;
  };

  const filteredOrders = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^#/, '');
    return orders.filter((o) => {
      const okStatus = !statusFilter || o.status === statusFilter;
      const okQuery =
        !q ||
        (o.customer_name + ' ' + o.customer_email + ' ' + o.id).toLowerCase().includes(q);
      return okStatus && okQuery;
    });
  }, [orders, query, statusFilter]);

  const allSelected = filteredOrders.length > 0 && selected.length === filteredOrders.length;
  const toggleAll = () =>
    setSelected(allSelected ? [] : filteredOrders.map((o) => o.id));
  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)));

  const bulkAdvance = async () => {
    await executeProtectedAction(async () => {
      // La spedizione richiede il tracking e manda l'email: si fa ordine per
      // ordine dalla finestra dedicata, non in blocco.
      let skippedShipping = 0;
      for (const id of selected) {
        const o = orders.find((x) => x.id === id);
        const meta = o && STATUS_META[o.status];
        if (!o || !meta?.next) continue;
        if (meta.next === 'shipped') {
          skippedShipping++;
          continue;
        }
        const ok = await updateOrder(o.id, { status: meta.next });
        if (ok) await logUpdateAction('orders', o.id, { status: o.status }, { status: meta.next });
      }
      toast.success('Stati aggiornati');
      if (skippedShipping > 0) {
        toast.info(
          skippedShipping === 1
            ? '1 ordine da spedire: usa "Segna spedito" per inserire il tracking'
            : `${skippedShipping} ordini da spedire: usa "Segna spedito" su ciascuno per inserire il tracking`
        );
      }
      setSelected([]);
      return true;
    });
  };

  const exportCsv = () => {
    const header = ['Ordine', 'Cliente', 'Email', 'Articoli', 'Totale', 'Stato', 'Pagamento', 'Data'];
    const rows = filteredOrders.map((o) => [
      shortId(o.id),
      o.customer_name,
      o.customer_email,
      (o.items ?? []).map((it) => `${it.quantity}× ${itemName(it.product_name, it.variant_label)}`).join('; '),
      (o.total_price + o.shipping_price).toFixed(2),
      STATUS_META[o.status].label,
      PAY_META[o.payment_status].label,
      new Date(o.created_at).toLocaleDateString('it-IT'),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ordini-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV di ${filteredOrders.length} ordini pronto`);
  };

  const navItems = (compact?: boolean) => (
    <>
      <NavButton compact={compact} active={view === 'oggi'} onClick={() => setView('oggi')} label="Oggi" count={pending.length + prep.length} />
      <NavButton compact={compact} active={view === 'ordini'} onClick={() => goToOrdini('')} label="Ordini" count={orders.length} />
      <NavButton compact={compact} active={view === 'catalogo'} onClick={() => setView('catalogo')} label="Catalogo" count={products.length} />
      <NavButton compact={compact} active={view === 'contenuti'} onClick={() => setView('contenuti')} label="Contenuti" />
      <NavButton
        compact={compact}
        active={view === 'impostazioni'}
        onClick={openSettings}
        label={compact ? 'Opzioni' : 'Impostazioni'}
        count={ordersSwitchLoading ? '…' : ordersEnabled ? 'APERTI' : 'CHIUSI'}
      />
    </>
  );

  const todayLabel = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-stretch bg-background font-sans text-foreground md:flex-row">
      {/* Barra di navigazione da telefono: la sidebar non ci sta */}
      <div className="sticky top-16 z-40 flex items-center gap-1 bg-brand px-2 py-1.5 md:hidden">
        {/* Sui telefoni più stretti le voci non ci stanno: la barra scorre. */}
        <nav className="flex min-w-0 flex-1 gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{navItems(true)}</nav>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label={`Aggiorna adesso (aggiornato ${lastSync})`}
          className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-white hover:bg-white/10 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sidebar */}
      <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-[244px] flex-none flex-col gap-7 bg-brand px-4 py-6 md:flex">
        <div className="flex items-center gap-2.5 px-2">
          <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full bg-white/15 font-display text-lg font-semibold text-white">
            AD
          </div>
          <div className="min-w-0">
            <div className="font-display text-[15px] font-semibold leading-5 text-white">DePiccoli</div>
            <div className="text-[9px] font-bold uppercase tracking-wider text-honey-200">Gestione</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">{navItems()}</nav>

        <div className="mt-auto flex flex-col gap-1.5 border-t border-white/20 pt-3.5">
          <div className="text-xs leading-[18px] text-honey-200">Aggiornato {lastSync}</div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-left text-[13px] font-semibold text-white underline decoration-1 underline-offset-2 disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna adesso
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex min-w-0 flex-1 flex-col gap-7 px-4 py-6 pb-16 sm:px-6 md:px-10 md:py-8">
        {view === 'oggi' && (
          <div className="flex flex-col gap-7">
            <header className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="mb-3.5 h-1 w-12 rounded-full bg-primary" />
                <h1 className="font-display text-3xl font-bold leading-[38px] tracking-tight md:text-4xl md:leading-[46px] text-foreground">Cosa c'è da fare</h1>
                <p className="mt-2 text-base leading-[26px] text-muted-foreground">
                  {todayLabel} — {pending.length + prep.length} ordini aspettano una tua mossa.
                </p>
              </div>
              <div className="flex w-full gap-7 rounded-xl bg-card px-5 py-4 sm:w-auto shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
                <div>
                  <div className="text-[10px] font-bold tracking-wide text-muted-foreground">INCASSATO</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-foreground">{eur(revenuePaid)}</div>
                </div>
                <div className="w-px bg-border" />
                <div>
                  <div className="text-[10px] font-bold tracking-wide text-muted-foreground">DA INCASSARE</div>
                  <div className="mt-1 font-display text-2xl font-semibold text-primary">{eur(revenueDue)}</div>
                </div>
              </div>
            </header>

            {disputed.length > 0 && (
              <section className="rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-5 py-4 text-red-900 dark:text-red-200">
                <h2 className="font-display text-xl font-semibold">
                  {disputed.length === 1 ? '1 pagamento contestato' : `${disputed.length} pagamenti contestati`}
                </h2>
                <p className="mt-1 text-sm">
                  Il cliente ha contestato l'addebito con la sua banca: non spedire e rispondi dalla dashboard Stripe.
                </p>
                <ul className="mt-2 space-y-1">
                  {disputed.map((o) => (
                    <li key={o.id} className="text-sm">
                      <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="font-bold underline">
                        {shortId(o.id)} · {o.customer_name}
                      </button>{' '}
                      — {DISPUTE_STATUS_LABELS[o.dispute_status!] ?? o.dispute_status}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { kicker: 'DA CONFERMARE', count: pending.length, desc: 'Ordini nuovi, ancora senza conferma.', cta: 'Confermali', tint: 'hsl(var(--danger))', go: 'pending' as OrderStatus },
                { kicker: 'DA PREPARARE', count: prep.length, desc: 'Confermati, da comporre in magazzino.', cta: 'Vai alla coda', tint: 'hsl(var(--primary))', go: 'processing' as OrderStatus },
                { kicker: 'PRONTI A PARTIRE', count: readyToShip.length, desc: 'Pagati e preparati: serve il tracking.', cta: 'Spedisci', tint: 'hsl(var(--eco-foreground))', go: 'processing' as OrderStatus },
              ].map((q) => (
                <button
                  key={q.kicker}
                  onClick={() => goToOrdini(q.go)}
                  className="flex flex-col gap-2.5 rounded-xl bg-card p-5 text-left shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40 transition-shadow hover:shadow-[0_10px_30px_-6px_rgba(180,83,9,.16)]"
                >
                  <span className="text-[10px] font-bold tracking-wide" style={{ color: q.tint }}>{q.kicker}</span>
                  <span className="font-display text-[44px] font-bold leading-[48px] text-foreground">{q.count}</span>
                  <span className="text-sm leading-[22px] text-foreground/80">{q.desc}</span>
                  <span className="mt-0.5 text-[13px] font-semibold text-primary">{q.cta} →</span>
                </button>
              ))}
            </section>

            <section className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
              <div className="overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
                <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-4">
                  <h2 className="font-display text-xl font-semibold text-foreground">Prossime spedizioni</h2>
                  <button onClick={() => goToOrdini('')} className="text-[13px] font-semibold text-primary">Vedi tutti</button>
                </div>
                {prep.length === 0 && (
                  <div className="px-5 py-7 text-sm text-muted-foreground">Niente da spedire. Tutti gli ordini pagati sono partiti.</div>
                )}
                {prep.slice(0, 5).map((o) => {
                  const meta = STATUS_META[o.status];
                  return (
                    <div key={o.id} className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3.5 last:border-b-0">
                      <div className="min-w-[140px] flex-1">
                        <div className="text-sm font-semibold text-foreground">{o.customer_name}</div>
                        <div className="text-xs leading-[18px] text-muted-foreground">
                          {shortId(o.id)} · {o.items_count} articoli · {o.shipping_address?.city}
                        </div>
                      </div>
                      <div className="text-sm font-bold text-foreground">{eur(o.total_price + o.shipping_price)}</div>
                      {meta.next && (
                        <button
                          onClick={() => advanceOrder(o)}
                          className="rounded-lg bg-action px-3.5 py-2 text-[13px] font-bold text-[#2F1500] shadow-[0_2px_10px_0_rgba(217,119,6,.25)] hover:bg-[#B45309] hover:text-white"
                        >
                          {meta.nextLabel}
                        </button>
                      )}
                      <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="px-1.5 py-2 text-[13px] font-semibold text-primary">
                        Apri
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col gap-6">
                <div className="rounded-xl bg-peach px-5 py-5">
                  <h2 className="mb-1 font-display text-xl font-semibold text-peach-foreground">Scorte in esaurimento</h2>
                  <p className="mb-3.5 text-[13px] leading-5 text-peach-foreground/75">
                    Formati sotto i 6 vasetti. Aggiorna la giacenza o togli il formato dalla vendita.
                  </p>
                  {lowStock.length === 0 && <div className="text-sm text-peach-foreground">Magazzino a posto.</div>}
                  {lowStock.map(({ product: p, variant: v }) => (
                    <div key={v.id} className="flex items-center justify-between gap-3 border-t border-black/10 py-2.5">
                      <div className="text-sm font-semibold text-peach-foreground">{variantName(p, v)}</div>
                      <div className="flex items-center gap-2.5">
                        <span className="text-[13px] font-bold text-danger">{v.stock === 0 ? 'esaurito' : v.stock + ' rimasti'}</span>
                        <button
                          onClick={() => restock(p, v)}
                          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:bg-primary/90"
                        >
                          +12
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl bg-card px-5 py-5 shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
                  <h2 className="mb-1 font-display text-xl font-semibold text-foreground">Pagamenti da verificare</h2>
                  <p className="mb-3 text-[13px] leading-5 text-muted-foreground">Bonifici e contrassegni ancora aperti.</p>
                  {unpaid.length === 0 && <div className="border-t border-border pt-2.5 text-sm text-muted-foreground">Tutti i pagamenti sono in ordine.</div>}
                  {unpaid.slice(0, 4).map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 border-t border-border py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">{o.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{shortId(o.id)} · {o.payment_method} · {ago(o.created_at)}</div>
                      </div>
                      <button
                        onClick={() => markPaid(o)}
                        className="rounded-lg bg-eco px-3 py-1.5 text-xs font-bold text-eco-foreground"
                      >
                        Segna incassato
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {view === 'ordini' && (
          <div className="flex flex-col gap-5">
            <header className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="mb-3.5 h-1 w-12 rounded-full bg-primary" />
                <h1 className="font-display text-3xl font-bold leading-[38px] tracking-tight md:text-4xl md:leading-[46px] text-foreground">Ordini</h1>
              </div>
              <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
                <div className="relative min-w-0 flex-1 sm:flex-none">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cerca cliente, email o numero ordine"
                    className="h-10 w-full rounded-full sm:w-[340px] border-0 bg-card pl-9 pr-3.5 text-sm text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] outline-none focus:shadow-[inset_0_0_0_2px_hsl(var(--ring))]"
                  />
                </div>
                <button
                  onClick={exportCsv}
                  className="flex h-10 items-center gap-1.5 rounded-lg bg-card px-4 text-[13px] font-bold text-primary shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:shadow-[inset_0_0_0_1px_hsl(var(--primary))]"
                >
                  <Download className="h-3.5 w-3.5" /> Esporta CSV
                </button>
              </div>
            </header>

            <div className="flex flex-wrap items-center gap-2">
              {STATUS_CHIPS.map((c) => (
                <button
                  key={c.key || 'all'}
                  onClick={() => setStatusFilter(c.key)}
                  className="min-h-10 rounded-full px-3.5 py-2 text-xs font-bold tracking-wide lg:min-h-0"
                  style={
                    statusFilter === c.key
                      ? { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }
                      : { background: 'hsl(var(--card))', color: 'hsl(var(--foreground) / 0.75)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }
                  }
                >
                  {c.label}
                </button>
              ))}
              <span className="ml-auto text-[13px] text-muted-foreground">
                {filteredOrders.length} di {orders.length} ordini
              </span>
            </div>

            {selected.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl bg-primary px-5 py-3 text-primary-foreground">
                <span className="text-sm font-semibold">
                  {selected.length} {selected.length === 1 ? 'ordine selezionato' : 'ordini selezionati'}
                </span>
                <div className="ml-auto flex flex-wrap gap-2">
                  <button onClick={bulkAdvance} className="rounded-lg bg-action px-3.5 py-2 text-[13px] font-bold text-[#2F1500]">
                    Avanza stato
                  </button>
                  <button onClick={() => setSelected([])} className="px-1.5 py-2 text-[13px] font-semibold text-honey-200">
                    Annulla
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
              {filteredOrders.length > 0 && (
                <label className="flex items-center gap-3 bg-muted px-4 py-3 text-[10px] font-bold tracking-wide text-muted-foreground lg:hidden">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-5 w-5 accent-primary" />
                  SELEZIONA TUTTI
                </label>
              )}
              <div className={`${ORDERS_GRID} items-center bg-muted py-3 text-[10px] font-bold tracking-wide text-muted-foreground`}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-primary" />
                <span>ORDINE</span>
                <span>CLIENTE</span>
                <span>TOTALE</span>
                <span>STATO</span>
                <span>PAGAMENTO</span>
                <span>AZIONE</span>
              </div>

              {ordersLoading && <div className="px-5 py-10 text-center text-sm text-muted-foreground">Caricamento ordini…</div>}

              {!ordersLoading && filteredOrders.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessun ordine con questi filtri.</div>
              )}

              {filteredOrders.map((o) => {
                const meta = STATUS_META[o.status];
                const pay = PAY_META[o.payment_status];
                const rowBg = { background: o.status === 'pending' ? 'hsl(var(--background))' : 'hsl(var(--card))' };
                const payment = (
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 flex-none rounded-full" style={{ background: pay.dot }} />
                    <span className="text-[13px] text-foreground/80">{pay.label}</span>
                    {isDisputeOpen(o.dispute_status) && (
                      <span className="rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-[10px] font-bold tracking-wide text-red-700 dark:text-red-200">CONTESTATO</span>
                    )}
                  </div>
                );
                const actions = (
                  <div className="flex items-center gap-1.5">
                    {meta.next && (
                      <button
                        onClick={() => advanceOrder(o)}
                        className="h-10 whitespace-nowrap rounded-lg bg-action px-3 text-xs font-bold text-[#2F1500] hover:bg-[#B45309] hover:text-white lg:h-auto lg:py-2"
                      >
                        {meta.nextLabel}
                      </button>
                    )}
                    <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="h-10 px-2 text-xs font-bold text-primary lg:h-auto lg:px-1 lg:py-2">
                      Dettaglio
                    </button>
                    {canDeleteOrder(o) ? (
                      <button
                        onClick={() => setDeletingOrder(o)}
                        className="ml-auto flex h-10 w-10 flex-none items-center justify-center rounded-lg text-danger hover:bg-destructive/10 lg:ml-0 lg:h-7 lg:w-7"
                        title="Elimina ordine"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      // Stesso spazio del cestino, così le righe restano allineate.
                      <span className="h-7 w-7 flex-none" title="Gli ordini pagati non si eliminano: annullali dal dettaglio" />
                    )}
                  </div>
                );
                return (
                  <React.Fragment key={o.id}>
                  <div className="flex flex-col gap-2.5 border-t border-border px-4 py-3.5 lg:hidden" style={rowBg}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(o.id)}
                        onChange={() => toggleOne(o.id)}
                        aria-label={`Seleziona ordine ${shortId(o.id)}`}
                        className="mt-0.5 h-5 w-5 flex-none accent-primary"
                      />
                      <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="min-w-0 flex-1 text-left">
                        <span className="flex items-baseline justify-between gap-3">
                          <span className="truncate text-sm font-semibold text-foreground">{o.customer_name}</span>
                          <span className="flex-none text-sm font-bold text-foreground">{eur(o.total_price + o.shipping_price)}</span>
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          <span className="font-bold text-primary">{shortId(o.id)}</span> · {ago(o.created_at)} · {o.items_count} articoli
                          {o.shipping_address?.city && <> · {o.shipping_address.city}</>}
                        </span>
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pl-8">
                      <StatusBadge status={o.status} />
                      {payment}
                    </div>
                    <div className="pl-8">{actions}</div>
                  </div>
                  <div className={`${ORDERS_GRID} items-center border-t border-border py-3.5`} style={rowBg}>
                    <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggleOne(o.id)} className="h-4 w-4 accent-primary" />
                    <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="text-left">
                      <span className="block text-sm font-bold text-primary">{shortId(o.id)}</span>
                      <span className="block text-xs text-muted-foreground">
                        {ago(o.created_at)} · {o.items_count} articoli
                      </span>
                    </button>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{o.customer_name}</div>
                      <div className="truncate text-xs text-muted-foreground">{o.shipping_address?.city}</div>
                    </div>
                    <div className="text-sm font-bold text-foreground">{eur(o.total_price + o.shipping_price)}</div>
                    <div>
                      <StatusBadge status={o.status} />
                    </div>
                    {payment}
                    {actions}
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {view === 'catalogo' && (
          <div className="flex flex-col gap-5">
            <header className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="mb-3.5 h-1 w-12 rounded-full bg-primary" />
                <h1 className="font-display text-3xl font-bold leading-[38px] tracking-tight md:text-4xl md:leading-[46px] text-foreground">Catalogo e giacenze</h1>
                <p className="mt-2 text-base leading-[26px] text-muted-foreground">
                  Magazzino: tutti i vasetti che hai. Online: quanti di questi metti in vendita sul sito. Una vendita online scala entrambi; per una vendita fuori sito abbassa solo il magazzino.
                </p>
              </div>
              <button
                onClick={() => setShowAddProduct((v) => !v)}
                className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90"
              >
                {showAddProduct ? 'Chiudi' : '+ Nuovo prodotto'}
              </button>
            </header>

            {showAddProduct && (
              <ProductForm onSubmit={handleAddProduct} onCancel={() => setShowAddProduct(false)} />
            )}

            <div className="overflow-x-auto rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
              <div className={`${CATALOG_GRID} bg-muted py-3 text-[10px] font-bold tracking-wide text-muted-foreground`}>
                <span>PRODOTTO</span>
                <span>PREZZO</span>
                <span>VENDUTI 30GG</span>
                <span>MAGAZZINO</span>
                <span>ONLINE</span>
                <span>IN VENDITA</span>
                <span>AZIONI</span>
              </div>

              {productsLoading && <div className="px-5 py-10 text-center text-sm text-muted-foreground">Caricamento prodotti…</div>}

              {!productsLoading && products.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessun prodotto in catalogo.</div>
              )}

              {products.map((p) => {
                const variants = allVariants(p);
                const saleBadge = (state: 'on' | 'out' | 'hidden') => (
                  <span
                    className="w-fit rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wide"
                    style={state === 'on' ? { background: 'hsl(var(--eco))', color: 'hsl(var(--eco-foreground))' } : { background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }}
                  >
                    {state === 'on' ? 'IN VENDITA' : state === 'out' ? 'ESAURITO' : 'NASCOSTO'}
                  </span>
                );
                const variantState = (v: ProductVariant) => (!p.active || !v.active ? 'hidden' : v.stock > 0 ? 'on' : 'out');
                const productActions = (
                  <div className="flex flex-none items-center gap-0.5 lg:gap-1.5">
                    <button
                      onClick={() => toggleVisibility(p)}
                      className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground lg:h-8 lg:w-8"
                      title={p.active ? 'Nascondi dal sito' : 'Mostra sul sito'}
                      aria-label={p.active ? `Nascondi ${p.name} dal sito` : `Mostra ${p.name} sul sito`}
                    >
                      {p.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={() => setEditingProduct(p)}
                      className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-primary hover:bg-primary/10 lg:h-8 lg:w-8"
                      title="Modifica prodotto e formati"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeletingProduct(p)}
                      className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-danger hover:bg-destructive/10 lg:h-8 lg:w-8"
                      title="Elimina prodotto"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
                const onlineHint = (v: ProductVariant) =>
                  v.stock < v.inventory && (
                    <span className="text-[10px] text-muted-foreground">{v.inventory - v.stock} solo in magazzino</span>
                  );
                const inventoryCell = (v: ProductVariant) => (
                  <QuantityCell label={`${variantName(p, v)}: magazzino`} value={v.inventory} onCommit={(n) => setInventory(p, v, n)} />
                );
                const onlineCell = (v: ProductVariant) => (
                  <QuantityCell
                    label={`${variantName(p, v)}: in vendita online`}
                    value={v.stock}
                    max={v.inventory}
                    danger={v.active && v.stock <= 5}
                    onCommit={(n) => setOnline(p, v, n)}
                  />
                );
                return (
                  <React.Fragment key={p.id}>
                  <div className="flex flex-col gap-3 border-t border-border px-4 py-4 lg:hidden">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-foreground">{p.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.category}
                          {!p.active && <span className="font-semibold text-foreground"> · nascosto dal sito</span>}
                        </div>
                      </div>
                      {productActions}
                    </div>
                    {variants.length === 0 && <div className="text-xs font-semibold text-danger">Nessun formato: non è in vendita</div>}
                    {variants.map((v) => (
                      <div key={v.id} className="flex flex-col gap-2 rounded-lg bg-muted/50 px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs text-muted-foreground">
                            <span className="text-sm font-bold text-foreground">{v.label}</span> · <span className="font-bold text-foreground">{eur(v.price)}</span> · {soldLast30[v.id] || 0} venduti in 30gg
                          </div>
                          {saleBadge(variantState(v))}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold tracking-wide text-muted-foreground">MAGAZZINO</span>
                            {inventoryCell(v)}
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold tracking-wide text-muted-foreground">ONLINE</span>
                            {onlineCell(v)}
                            {onlineHint(v)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={`${CATALOG_GRID} items-center border-t border-border pt-3.5 pb-1.5`}>
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-foreground">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.category}
                        {!p.active && <span className="font-semibold text-foreground"> · nascosto dal sito</span>}
                        {variants.length === 0 && <span className="font-semibold text-danger"> · nessun formato: non è in vendita</span>}
                      </div>
                    </div>
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    {productActions}
                  </div>
                  {variants.map((v) => (
                    <div key={v.id} className={`${CATALOG_GRID} items-center py-2`}>
                      <div className="pl-4 text-sm font-semibold text-foreground/80">{v.label}</div>
                      <div className="text-sm font-bold text-foreground">{eur(v.price)}</div>
                      <div className="text-sm text-foreground/80">{soldLast30[v.id] || 0} pezzi</div>
                      {inventoryCell(v)}
                      <div className="flex flex-col gap-0.5">
                        {onlineCell(v)}
                        {onlineHint(v)}
                      </div>
                      {saleBadge(variantState(v))}
                      <span />
                    </div>
                  ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {view === 'contenuti' && (
          <div className="flex max-w-5xl flex-col gap-6">
            <header>
              <div className="mb-3.5 h-1 w-12 rounded-full bg-primary" />
              <h1 className="font-display text-3xl font-bold leading-[38px] tracking-tight md:text-4xl md:leading-[46px] text-foreground">Contenuti del sito</h1>
              <p className="mt-2 text-base leading-[26px] text-muted-foreground">
                Testi e foto della home, contatti e social. Ogni sezione si salva da sola.
              </p>
            </header>
            <SiteContentEditor executeProtectedAction={executeProtectedAction} logUpdateAction={logUpdateAction} />
          </div>
        )}

        {view === 'impostazioni' && (
          <div className="flex max-w-3xl flex-col gap-6">
            <header>
              <div className="mb-3.5 h-1 w-12 rounded-full bg-primary" />
              <h1 className="font-display text-3xl font-bold leading-[38px] tracking-tight md:text-4xl md:leading-[46px] text-foreground">Impostazioni</h1>
            </header>

            <section className="rounded-xl bg-card p-5 shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="font-display text-xl font-semibold text-foreground">Ordini online</h2>
                  <p className="mt-1 text-sm leading-[22px] text-muted-foreground">
                    {ordersEnabled
                      ? 'Aperti: i clienti possono pagare e ordinare dal sito.'
                      : 'Chiusi: i clienti vedono i prodotti ma non possono completare un ordine.'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wide"
                    style={ordersEnabled ? { background: 'hsl(var(--eco))', color: 'hsl(var(--eco-foreground))' } : { background: 'hsl(var(--secondary))', color: 'hsl(var(--danger))' }}
                  >
                    {ordersSwitchLoading ? '…' : ordersEnabled ? 'APERTI' : 'CHIUSI'}
                  </span>
                  <button
                    onClick={() => setConfirmOrdersSwitch(!ordersEnabled)}
                    disabled={ordersSwitchLoading}
                    className={`h-10 rounded-lg px-4 text-[13px] font-bold disabled:opacity-60 ${
                      ordersEnabled
                        ? 'bg-muted text-danger shadow-[inset_0_0_0_1px_hsl(var(--danger)/0.3)]'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                  >
                    {ordersEnabled ? 'Chiudi gli ordini' : 'Apri gli ordini'}
                  </button>
                </div>
              </div>
            </section>

            <ShippingSettings executeProtectedAction={executeProtectedAction} logUpdateAction={logUpdateAction} />

            <section className="overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
              <div className="border-b border-border px-6 py-5">
                <h2 className="font-display text-xl font-semibold text-foreground">Amministratori</h2>
                <p className="mt-1 text-sm leading-[22px] text-muted-foreground">
                  La persona deve prima registrarsi sul sito con la sua email. Al primo accesso all'area admin
                  dovrà configurare l'autenticazione a due fattori.
                </p>
              </div>

              {adminsLoading && admins.length === 0 && (
                <div className="px-6 py-5 text-sm text-muted-foreground">Caricamento…</div>
              )}
              {admins.map((a) => (
                <div key={a.user_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-3.5">
                  <div className="min-w-0">
                    <div className="break-all text-sm font-semibold text-foreground">{a.email}</div>
                    <div className="text-xs text-muted-foreground">
                      Admin dal {new Date(a.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  {a.is_self ? (
                    <span className="text-xs font-bold tracking-wide text-muted-foreground">TU</span>
                  ) : (
                    <button
                      onClick={() => setRemovingAdmin({ user_id: a.user_id, email: a.email })}
                      className="rounded-lg px-3 py-1.5 text-[13px] font-semibold text-danger hover:bg-destructive/10"
                    >
                      Rimuovi
                    </button>
                  )}
                </div>
              ))}

              <form onSubmit={handleAddAdmin} className="flex flex-wrap items-center gap-3 px-6 py-5">
                <label htmlFor="new-admin-email" className="sr-only">Email del nuovo amministratore</label>
                <input
                  id="new-admin-email"
                  type="email"
                  required
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="email di un utente registrato"
                  className="h-10 min-w-[200px] flex-1 rounded-lg border border-border bg-card px-3 text-sm"
                />
                <button
                  type="submit"
                  disabled={addingAdmin || !newAdminEmail.trim()}
                  className="h-10 w-full rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60 sm:w-auto"
                >
                  {addingAdmin ? 'Aggiungo…' : 'Rendi amministratore'}
                </button>
              </form>
            </section>
          </div>
        )}
      </main>

      {editingProduct && (
        <ProductEditOverlay
          product={editingProduct}
          isOpen
          onSave={handleSaveProductEdit}
          onClose={() => setEditingProduct(null)}
        />
      )}

      {deletingProduct && (
        <ConfirmationModal
          isOpen
          onClose={() => setDeletingProduct(null)}
          onConfirm={() => handleDeleteProduct(deletingProduct)}
          title="Conferma eliminazione"
          message={
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <p>Sei sicuro di voler eliminare il prodotto:</p>
              <p className="font-semibold text-foreground">"{deletingProduct.name}"</p>
              <p className="text-sm text-muted-foreground">
                Questa azione è irreversibile. Il prodotto verrà rimosso permanentemente dal database.
              </p>
            </div>
          }
          confirmText="Elimina definitivamente"
          cancelText="Annulla"
          type="danger"
        />
      )}

      {deletingOrder && (
        <ConfirmationModal
          isOpen
          onClose={() => setDeletingOrder(null)}
          onConfirm={() => handleDeleteOrder(deletingOrder)}
          title="Conferma eliminazione"
          message={
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <p>Sei sicuro di voler eliminare l'ordine:</p>
              <p className="font-semibold text-foreground">{shortId(deletingOrder.id)} — {deletingOrder.customer_name}</p>
              <p className="text-sm text-muted-foreground">
                Questa azione è irreversibile. L'ordine non è mai stato pagato e verrà rimosso dal database;
                l'eventuale giacenza prenotata torna in vendita.
              </p>
            </div>
          }
          confirmText="Elimina definitivamente"
          cancelText="Annulla"
          type="danger"
        />
      )}


      {shippingOrder && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2F1500]/40 p-4 backdrop-blur-[2px]"
          onClick={(e) => e.target === e.currentTarget && !shippingBusy && setShippingOrder(null)}
        >
          <form
            onSubmit={handleShip}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ship-title"
            className="w-full max-w-md rounded-xl bg-card p-6 shadow-[0_24px_60px_-12px_rgba(47,21,0,.35)] ring-1 ring-border/40"
          >
            <div className="mb-2.5 h-1 w-10 rounded-full bg-primary" />
            <h2 id="ship-title" className="font-display text-2xl font-semibold text-foreground">Segna come spedito</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Ordine {shortId(shippingOrder.id)} · {shippingOrder.customer_name}
            </p>

            {isDisputeOpen(shippingOrder.dispute_status) && (
              <p className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm font-semibold text-red-800 dark:text-red-200">
                Attenzione: il pagamento di questo ordine è contestato. Di norma non va spedito finché la
                contestazione non è risolta.
              </p>
            )}

            <label htmlFor="ship-tracking" className="mt-5 mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Numero di tracking *
            </label>
            <input
              id="ship-tracking"
              autoFocus
              required
              value={shipTracking}
              onChange={(e) => setShipTracking(e.target.value)}
              placeholder="Es. AB123456789IT"
              className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm"
            />
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              {['shipped', 'delivered', 'completed'].includes(shippingOrder.status)
                ? "L'ordine risulta già spedito: il tracking viene aggiornato, senza mandare un'altra email."
                : 'Al salvataggio il cliente riceve una email con questo numero e il link per seguire il pacco.'}
            </p>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShippingOrder(null)}
                disabled={shippingBusy}
                className="h-10 rounded-lg bg-card px-4 text-[13px] font-bold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:bg-muted disabled:opacity-60"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={shippingBusy || !shipTracking.trim()}
                className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {shippingBusy ? 'Salvataggio…' : 'Segna spedito e avvisa il cliente'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmOrdersSwitch !== null && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmOrdersSwitch(null)}
          onConfirm={() => handleOrdersSwitch(confirmOrdersSwitch)}
          title={confirmOrdersSwitch ? 'Aprire gli ordini online?' : 'Chiudere gli ordini online?'}
          message={
            <p className="text-center">
              {confirmOrdersSwitch
                ? 'Da subito i clienti potranno pagare e ordinare dal sito. Assicurati che Stripe sia in modalità live.'
                : 'Da subito nessun cliente potrà completare un nuovo ordine. Gli ordini già ricevuti non cambiano.'}
            </p>
          }
          confirmText={confirmOrdersSwitch ? 'Apri gli ordini' : 'Chiudi gli ordini'}
          cancelText="Annulla"
          type={confirmOrdersSwitch ? 'info' : 'danger'}
        />
      )}

      {removingAdmin && (
        <ConfirmationModal
          isOpen
          onClose={() => setRemovingAdmin(null)}
          onConfirm={() => handleRemoveAdmin(removingAdmin.user_id, removingAdmin.email)}
          title="Rimuovere amministratore?"
          message={
            <p className="text-center">
              <span className="font-semibold">{removingAdmin.email}</span> perderà subito l'accesso all'area admin.
              Il suo account cliente resta attivo.
            </p>
          }
          confirmText="Rimuovi"
          cancelText="Annulla"
          type="danger"
        />
      )}
    </div>
  );
};

export default AdminDashboard;
